import { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useParams } from "react-router";
import { mdiDeleteOutline, mdiEyeOutline, mdiPencilOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";

import ConfirmationModal from "components/ConfirmationModal";
import Select from "components/Select";
import { VISIBILITY_LIST } from "constants/haulpay-broker";
import { showErrorMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import { IFRAttachment } from "models/HaulPay";
import { deleteFRAttachmentAPI } from "services/api/haulpay";
import styles from "./style.module.scss";

interface IProps {
  data: IFRAttachment;
  setAttachments: (value: any) => void;
  setFile?: (value: IFRAttachment[]) => void;
  name?: string;
}

export default function FRAttachmentItem({
  data,
  setAttachments,
  setFile,
  name
}: Readonly<IProps>) {
  const { fundingRequestId } = useParams();
  const { isBroker } = useUserFeature();
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();
  const { control } = useFormContext();

  const editAttachment = () => {
    if (setFile) {
      setFile([data]);
    }
  };

  const deleteFile = async () => {
    if (fundingRequestId) {
      try {
        await deleteFRAttachmentAPI(fundingRequestId, data.id);
        toggleConfirmModal();
        setAttachments((prev: any) => prev.filter((item: IFRAttachment) => item.id !== data.id));
      } catch {
        showErrorMessage("Failed to delete attachment.");
      }
    }
  };

  const previewAttachment = useCallback(() => {
    const newWindow = window.open(data?.downloadUrl, "_blank");
    if (newWindow) {
      newWindow.focus();
    } else {
      showErrorMessage("Failed to open the window. It might have been blocked by the browser.");
    }
  }, [data]);

  return (
    <div className={styles.fundingRequestItemContainer}>
      <div className={styles.fileData}>
        <div className="flex-1 desktop:flex-none">
          <span className="text-placeholder">File Name</span>
          <span className="underline text-info">
            {data.filename}
            <br />
            {data.uploaded}
          </span>
        </div>
        <div className="flex-1">
          <span className="text-placeholder">Category</span>
          <span>{data.category}</span>
        </div>
      </div>
      <div className="desktop:flex-none">
        {isBroker ? <span className="text-placeholder">Visibility</span> : null}
        <form className={styles.visibility}>
          {isBroker ? (
            <Controller
              name={`${name}.visibleTo`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  options={VISIBILITY_LIST}
                  value={VISIBILITY_LIST.find((data) => data.value === value?.join(","))}
                  onChange={(object) => {
                    onChange(object?.value?.split(","));
                  }}
                  className={styles.list}
                  btnClass="mobile:!p-1.5 !p-2 mobile:!pr-10 !pr-14 !rounded !bg-transparent !border-tertiary mobile:!w-24 mobile:!text-xs"
                />
              )}
            />
          ) : null}

          <button type="button" className={styles.iconButton} onClick={previewAttachment}>
            <Icon path={mdiEyeOutline} />
          </button>

          <button type="button" className={styles.iconButton} onClick={editAttachment}>
            <Icon path={mdiPencilOutline} />
          </button>

          <button type="button" className={styles.iconButton} onClick={toggleConfirmModal}>
            <Icon path={mdiDeleteOutline} />
          </button>
        </form>
      </div>

      <ConfirmationModal
        content="Are you sure you want to delete this attachment?"
        isConfirmModalOpen={isConfirmModalOpen}
        toggleConfirmModal={toggleConfirmModal}
        onOK={deleteFile}
      />
    </div>
  );
}
